// Firebase Error Handler Service
import { getApp } from 'firebase/app'
import {
  enableIndexedDbPersistence,
  disableNetwork,
  enableNetwork,
  clearIndexedDbPersistence,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  CACHE_SIZE_UNLIMITED
} from 'firebase/firestore'
import { db, app } from './config'

const env = typeof import.meta !== 'undefined' && import.meta?.env ? import.meta.env : {}
const parseBooleanEnv = (value, fallback = false) => {
  if (value == null) return fallback
  const normalized = String(value).trim().toLowerCase()
  if (!normalized) return fallback
  return ['1', 'true', 'yes', 'on'].includes(normalized)
}

const isUsingFirebaseEmulators = (() => {
  const requested = parseBooleanEnv(env.VITE_FIREBASE_USE_EMULATORS, false)
  const isProdBuild = Boolean(env.PROD)
  const allowProdEmulators = parseBooleanEnv(env.VITE_FIREBASE_ALLOW_PROD_EMULATORS, false)
  return requested && (!isProdBuild || allowProdEmulators)
})()

// Firebase error codes and their user-friendly messages
const errorMessages = {
  // Auth errors
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/email-already-in-use': 'This email address is already in use.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/account-exists-with-different-credential':
    'An account already exists with the same email address but different sign-in credentials.',
  'auth/operation-not-allowed': 'This sign-in method is not allowed. Please contact support.',

  // Firestore errors
  'permission-denied': "You don't have permission to access this resource.",
  unavailable: 'The service is currently unavailable. Please try again later.',
  'failed-precondition':
    "The operation was rejected because the system is not in a state required for the operation's execution.",
  'resource-exhausted': 'The operation was rejected because it would exceed resource quotas.',
  'bad-request': 'The server rejected the request as invalid. Please try refreshing the page.',

  // Storage errors
  'storage/unauthorized': "User doesn't have permission to access the object.",
  'storage/canceled': 'User canceled the upload.',
  'storage/unknown': 'Unknown error occurred during upload.',

  // Generic errors
  'network-request-failed': 'A network error has occurred. Please check your connection.',
  timeout: 'The operation has timed out. Please try again.',
  cancelled: 'The operation was cancelled.',
  'rpc-error': 'A server error occurred. Please try again later.'
}

// Keep track of connection state
let isNetworkDisabled = false
let connectionRetryCount = 0
const MAX_RETRIES = 5
const RETRY_DELAY_BASE = 5000 // 5 seconds base delay
let lastBadRequestTime = 0
const BAD_REQUEST_THRESHOLD = 3 // How many bad requests before taking drastic action
let badRequestCount = 0

// WebChannel specific settings
const WEBCHANNEL_SETTINGS = {
  maxRetries: 3,
  forceRetry: true,
  retryDelayMs: 2000,
  maxBackoffMs: 10000
}

// MediaPipe/TFLite runtime messages that are noisy but non-actionable in app logs.
const BENIGN_VISION_LOG_PATTERNS = [
  /OpenGL error checking is disabled/i,
  /Graph successfully started running/i,
  /Created TensorFlow Lite XNNPACK delegate for CPU/i,
  /Feedback manager requires a model with a single signature inference/i,
  /Sets FaceBlendshapesGraph acceleration to xnnpack by default/i
]

// Add extra patterns commonly emitted by MediaPipe/TFLite WASM builds
BENIGN_VISION_LOG_PATTERNS.push(/GL version:/i)
BENIGN_VISION_LOG_PATTERNS.push(/gl_context\.cc/i)
BENIGN_VISION_LOG_PATTERNS.push(/segmentation_postprocessor_gl\.cc/i)
BENIGN_VISION_LOG_PATTERNS.push(/vision_wasm_internal\.js/i)
const shouldSuppressBenignVisionLog = (args = []) => {
  for (const arg of args) {
    if (typeof arg !== 'string') continue
    for (const pattern of BENIGN_VISION_LOG_PATTERNS) {
      if (pattern.test(arg)) return true
    }
  }
  return false
}

// Connection recovery function with progressive strategies
const attemptConnectionRecovery = async (errorType) => {
  if (isUsingFirebaseEmulators) return

  if (connectionRetryCount >= MAX_RETRIES) {
    if (errorType === 'bad-request' && badRequestCount >= BAD_REQUEST_THRESHOLD) {
      await resetFirestoreConnection()
      badRequestCount = 0
      connectionRetryCount = 0
      return
    }
    return
  }

  connectionRetryCount++
  const delay = Math.min(
    RETRY_DELAY_BASE * Math.pow(2, connectionRetryCount - 1),
    WEBCHANNEL_SETTINGS.maxBackoffMs
  )

  setTimeout(async () => {
    try {
      // First disable network
      if (!isNetworkDisabled) {
        await disableNetwork(db)
        isNetworkDisabled = true
      }

      // Wait for a bit to ensure all connections are closed
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Re-enable network
      await enableNetwork(db)
      isNetworkDisabled = false

      if (errorType === 'bad-request') {
        badRequestCount++
        const now = Date.now()

        if (
          badRequestCount >= BAD_REQUEST_THRESHOLD ||
          (lastBadRequestTime > 0 && now - lastBadRequestTime < 30000)
        ) {
          await resetFirestoreConnection()
          badRequestCount = 0
        }

        lastBadRequestTime = now
      }
    } catch (error) {
      // Schedule another retry if we still haven't hit the limit
      if (connectionRetryCount < MAX_RETRIES) {
        attemptConnectionRecovery(errorType)
      }
    }
  }, delay)
}

// Reset the Firestore connection completely
const resetFirestoreConnection = async () => {
  if (isUsingFirebaseEmulators) {
    // Keep emulator mode simple: avoid persistence/network reset loops that
    // can leave SDK state offline during local development.
    return false
  }

  try {
    await disableNetwork(db)

    try {
      await clearIndexedDbPersistence(db)
    } catch (clearError) {
      // This error is expected if the database is open in other tabs
    }

    // Wait a bit for resources to fully release
    await new Promise((resolve) => setTimeout(resolve, 2000))

    // Re-enable network on current instance
    try {
      await enableNetwork(db)
    } catch (enableError) {
      console.error('Could not re-enable existing network:', enableError)
    }

    isNetworkDisabled = false
    return true
  } catch (error) {
    console.error('Failed to reset Firestore connection:', error)
    return false
  }
}

// Initialize Firebase logging
const initFirebaseErrorHandling = () => {
  // Set up our own Firebase error monitoring
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason
    if (error && error.name === 'FirebaseError') {
      handleFirebaseError(error)
      // Prevent the error from propagating further
      event.preventDefault()
    }
  })

  // Set up an error handler for Firebase-related errors
  const originalConsoleError = console.error
  console.error = function (...args) {
    if (shouldSuppressBenignVisionLog(args)) return

    if (args.length > 0) {
      const firstArg = args[0]
      if (
        typeof firstArg === 'string' &&
        (firstArg.includes('Firebase') ||
          firstArg.includes('Firestore') ||
          firstArg.includes('WebChannelConnection'))
      ) {
        // This is likely a Firebase error, process it
        handleFirebaseConsoleMessage('error', args)
      }
    }
    // Call the original console.error
    originalConsoleError.apply(console, args)
  }

  // Also handle warnings
  const originalConsoleWarn = console.warn
  console.warn = function (...args) {
    if (shouldSuppressBenignVisionLog(args)) return

    if (args.length > 0) {
      const firstArg = args[0]
      if (
        typeof firstArg === 'string' &&
        (firstArg.includes('Firebase') ||
          firstArg.includes('Firestore') ||
          firstArg.includes('WebChannelConnection'))
      ) {
        // This is likely a Firebase warning, process it
        handleFirebaseConsoleMessage('warn', args)
      }
    }
    // Call the original console.warn
    originalConsoleWarn.apply(console, args)
  }

  // Keep info channel clean from known benign MediaPipe/TFLite noise.
  const originalConsoleInfo = console.info
  console.info = function (...args) {
    if (shouldSuppressBenignVisionLog(args)) return
    originalConsoleInfo.apply(console, args)
  }

  // Also quiet benign messages printed with console.log (WASM and native libs often use log)
  const originalConsoleLog = console.log
  console.log = function (...args) {
    if (shouldSuppressBenignVisionLog(args)) return
    originalConsoleLog.apply(console, args)
  }
  // Add network status listeners to detect connection changes.
  // Skip in emulator mode to avoid forcing Firestore offline when localhost
  // services are still available.
  if (!isUsingFirebaseEmulators) {
    window.addEventListener('online', handleOnlineStatusChange)
    window.addEventListener('offline', handleOnlineStatusChange)
  }
}

// Handle online/offline status changes
const handleOnlineStatusChange = async (event) => {
  if (isUsingFirebaseEmulators) return

  const isOnline = navigator.onLine

  if (isOnline && isNetworkDisabled) {
    try {
      await enableNetwork(db)
      isNetworkDisabled = false
      connectionRetryCount = 0
      badRequestCount = 0 // Reset bad request count when we regain connection
    } catch (error) {
      console.error('Failed to re-enable Firestore network:', error)
    }
  } else if (!isOnline && !isNetworkDisabled) {
    try {
      await disableNetwork(db)
      isNetworkDisabled = true
    } catch (error) {
      console.error('Failed to disable Firestore network:', error)
    }
  }
}

// Process Firebase console messages with enhanced WebChannel handling
const handleFirebaseConsoleMessage = (level, args) => {
  const message = args[0]

  if (message.includes('WebChannelConnection')) {
    if (message.includes('400') || message.includes('Bad Request')) {
      if (navigator.onLine) {
        attemptConnectionRecovery('bad-request')
      }
      return
    }

    if (message.includes('transport errored')) {
      if (navigator.onLine) {
        attemptConnectionRecovery('transport-error')
      }
      return
    }
  }

  // Handle other Firebase errors with the existing logic
  if (level === 'error' || (level === 'warn' && message.includes('transport errored'))) {
    if (navigator.onLine) {
      attemptConnectionRecovery('transport-error')
    }
  }
}

// Generic Firebase error handler
const handleFirebaseError = (error) => {
  if (!error) return

  const errorCode = error.code || ''
  const errorMessage = error.message || ''

  // Special handling for 400 Bad Request errors
  if (errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
    // For 400 Bad Request errors, try specialized recovery if we're online
    if (navigator.onLine) {
      attemptConnectionRecovery('bad-request')
    }
  }
  // Log specific information about WebChannel connection errors
  else if (
    errorMessage.includes('WebChannelConnection') ||
    errorMessage.includes('network-request-failed') ||
    errorMessage.includes('unavailable')
  ) {
    // For serious connection errors, try recovery if we're supposed to be online
    if (navigator.onLine) {
      attemptConnectionRecovery('connection-error')
    }
  } else {
    // Log other Firebase errors with contextual information
    console.error(`[Firebase Error] ${errorCode ? `(${errorCode})` : ''} ${errorMessage}`, error)
  }

  return getFirebaseErrorMessage(error)
}

/**
 * Get a user-friendly error message for a Firebase error code
 * @param {Error|string} error - Firebase error object or error code
 * @returns {string} User-friendly error message
 */
const getFirebaseErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred.'

  let code = typeof error === 'string' ? error : error.code || ''

  // Check for 400 Bad Request in message
  if (error.message && (error.message.includes('400') || error.message.includes('Bad Request'))) {
    return errorMessages['bad-request']
  }

  // Return a user-friendly message if available, otherwise return the original message or a default
  if (errorMessages[code]) {
    return errorMessages[code]
  }

  return error.message || 'An unexpected error occurred. Please try again.'
}

/**
 * Handle Firestore transaction errors with retry logic
 * @param {Function} operationFn - The Firestore operation function to execute
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} initialDelay - Initial delay in ms before retry (default: 1000)
 * @returns {Promise<any>} Result of the operation or throws error after max retries
 */
const withRetry = async (operationFn, maxRetries = 3, initialDelay = 1000) => {
  let lastError
  let retries = 0

  while (retries <= maxRetries) {
    try {
      return await operationFn()
    } catch (error) {
      lastError = error

      // Only retry on specific errors that might be transient
      const retryableErrors = [
        'unavailable',
        'resource-exhausted',
        'deadline-exceeded',
        'network-request-failed'
      ]

      const isRetryable =
        retryableErrors.includes(error.code) ||
        (error.message && error.message.includes('network')) ||
        (error.message && error.message.includes('WebChannelConnection'))

      if (!isRetryable) {
        throw error // Don't retry for non-retryable errors
      }

      if (retries === maxRetries) {
        break // Exit if max retries reached
      }

      // Exponential backoff: delay increases with each retry
      const delay = initialDelay * Math.pow(2, retries)

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay))

      retries++
    }
  }

  // If we get here, all retries failed
  throw lastError
}

export {
  initFirebaseErrorHandling,
  getFirebaseErrorMessage,
  withRetry,
  handleFirebaseError,
  // Exported for callers that need to attempt a full persistence reset
  resetFirestoreConnection
}
